'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  IconApi,
  IconMessage,
  IconPhone,
  IconPencilPlus,
  IconSettings,
  IconRobot,
  IconBuildingStore,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useAppModeStore } from '@/stores/appModeStore';
import classes from './BottomTabs.module.css';

const fullTabs = [
  { href: '/build', icon: IconPencilPlus, label: 'Build' },
  { href: '/agents', icon: IconRobot, label: 'Agents' },
  { href: '/marketplace', icon: IconBuildingStore, label: 'Marketplace' },
  { href: '/governance', icon: IconShieldCheck, label: 'Governance' },
  { href: '/settings', icon: IconSettings, label: 'Settings' },
  { href: '/docs', icon: IconApi, label: 'Docs' },
];

const appModeTabs = [
  { href: '/chat', icon: IconMessage, label: 'Chat' },
  { href: '/voice', icon: IconPhone, label: 'Voice' },
  { href: '/settings', icon: IconSettings, label: 'Settings' },
];

const appModeTabsNoVoice = appModeTabs.filter((t) => t.href !== '/voice');

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + '/');
}

function useKeyboardVisible() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const initialHeight = window.innerHeight;
    const onResize = () => {
      setVisible(initialHeight - vv.height > 150);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  return visible;
}

export function BottomTabs() {
  const pathname = usePathname();
  const keyboardVisible = useKeyboardVisible();
  const isAppMode = useAppModeStore((s) => s.isAppMode);
  const voiceDisabled = useAppModeStore((s) => s.appConfig?.voice_disabled);
  const tabs = isAppMode
    ? voiceDisabled
      ? appModeTabsNoVoice
      : appModeTabs
    : fullTabs;

  if (keyboardVisible) return null;

  return (
    <nav className={classes.bar}>
      {tabs.map(({ href, icon: Icon, label }) => (
        <Link
          key={href}
          href={href}
          className={classes.tab}
          data-active={isActive(pathname, href) || undefined}
        >
          <Icon size={20} stroke={1.5} />
          <span className={classes.label}>{label}</span>
        </Link>
      ))}
    </nav>
  );
}
